<template>
  <div class="approval-page pa-4">
    <!-- Gradient Header -->
    <v-card elevation="12" class="header-card mb-6 overflow-hidden">
      <div class="header-gradient">
        <div class="header-backdrop-effect"></div>
        <div class="header-content pa-5 pa-sm-7">
          <v-row align="center" justify="space-between">
            <v-col cols="12" md="8" class="d-flex align-center">
              <div class="header-icon-box">
                <v-icon size="36px" color="white">mdi-school</v-icon>
              </div>
              <div class="ml-4">
                <h1 class="header-title white--text mb-0">Student Scholarship Preview</h1>
                <p class="header-subtitle white--text mb-0">
                  Review and approve student scholarship application
                </p>
              </div>
            </v-col>
            <v-col cols="12" md="4" class="d-flex justify-md-end mt-3 mt-md-0">
              <v-btn class="back-btn" rounded @click="goBack">
                <v-icon left>mdi-arrow-left</v-icon> Back to List
              </v-btn>
            </v-col>
          </v-row>
        </div>
      </div>
    </v-card>

    <!-- Loading -->
    <v-overlay :value="LoadingFlag">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <!-- Student Details + Bank Details Row -->
    <v-row class="mb-0">
      <!-- Student Details -->
      <v-col cols="12" md="8">
        <v-card class="section-card mb-4 fill-height">
          <div class="section-header">
            <v-icon color="white" class="mr-2">mdi-school</v-icon>
            <span class="section-header-title">Student Details</span>
          </div>
          <v-card-text class="pa-4">
            <v-row align="center">
              <v-col cols="12" sm="3" class="text-center">
                <v-img :src="records.StudentImagePath" height="160" contain class="student-img"></v-img>
              </v-col>
              <v-col cols="12" sm="9">
                <v-row>
                  <v-col cols="12" sm="6"><span class="detail-label">Application No:</span> <span class="detail-value">{{ records.ApplicationNumber }}</span></v-col>
                  <v-col cols="12" sm="6"><span class="detail-label">Name:</span> <span class="detail-value">{{ records.StudentName }}</span></v-col>
                  <v-col cols="12" sm="6"><span class="detail-label">Gender:</span> <span class="detail-value">{{ records.Gender }}</span></v-col>
                  <v-col cols="12" sm="6"><span class="detail-label">School:</span> <span class="detail-value">{{ records.StudentSchoolName }}</span></v-col>
                  <v-col cols="12" sm="6"><span class="detail-label">Parent Name:</span> <span class="detail-value">{{ records.StudentParentName }}</span></v-col>
                  <v-col cols="12" sm="6"><span class="detail-label">Parent Contact:</span> <span class="detail-value">{{ records.ParentContact }}</span></v-col>
                  <v-col cols="12" sm="6"><span class="detail-label">Category:</span> <span class="detail-value">{{ records.Category }}</span></v-col>
                  <v-col cols="12" sm="6"><span class="detail-label">Sponsor Name:</span> <span class="detail-value">{{ records.SponserName }}</span></v-col>
                  <v-col cols="12" sm="6">
                    <span class="detail-label">Scholarship Type:</span>
                    <v-chip small :color="records.StudentScholarshipTypeColor" class="white--text ml-1">{{ records.StudentScholarshipTypeTxt }}</v-chip>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <span class="detail-label">Approval Status:</span>
                    <v-chip small :color="records.StudentApprovalStatusColor" class="white--text ml-1">{{ records.StudentApprovalStatusTxt }}</v-chip>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <span class="detail-label">Application Status:</span>
                    <v-chip small :color="records.ReportStatusColor" class="white--text ml-1">{{ records.ReportStatusTxt }}</v-chip>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Bank Details (next to Student Details) -->
      <v-col cols="12" md="4">
        <v-card class="section-card mb-4 fill-height">
          <div class="section-header">
            <v-icon color="white" class="mr-2">mdi-bank-outline</v-icon>
            <span class="section-header-title">Bank Details</span>
          </div>
          <v-card-text class="pa-4">
            <v-row>
              <v-col cols="12" sm="6"><span class="detail-label">Account Holder:</span> <span class="detail-value">{{ records.AccountHolderName }}</span></v-col>
              <v-col cols="12" sm="6"><span class="detail-label">Account Number:</span> <span class="detail-value">{{ records.AccountNumber }}</span></v-col>
              <v-col cols="12" sm="6"><span class="detail-label">Bank Name:</span> <span class="detail-value">{{ records.BankName }}</span></v-col>
              <v-col cols="12" sm="6"><span class="detail-label">Branch Name:</span> <span class="detail-value">{{ records.BranchName }}</span></v-col>
              <v-col cols="12" sm="6"><span class="detail-label">IFSC Code:</span> <span class="detail-value">{{ records.IfscCode }}</span></v-col>
              <v-col cols="12" sm="6">
                <span class="detail-label">Account Status:</span>
                <v-chip small :color="records.AccountStatusTxtColor" class="white--text ml-1">{{ records.AccountStatusTxt }}</v-chip>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row class="mb-0">
      <!-- Mark Details -->
      <v-col cols="12" md="4">
        <v-card class="section-card mb-4 fill-height">
          <div class="section-header">
            <v-icon color="white" class="mr-2">mdi-chart-bar</v-icon>
            <span class="section-header-title">Mark Details</span>
          </div>
          <v-card-text class="pa-4">
            <v-row>
              <v-col cols="12" sm="12"><span class="detail-label">Class:</span> <span class="detail-value">{{ records.ClassName }} th std</span></v-col>
              <v-col cols="12" sm="12"><span class="detail-label">Percentage:</span> <span class="detail-value">{{ records.Percentage }}%</span></v-col>
              <v-col cols="12" sm="12"><span class="detail-label">Student Type:</span> <span class="detail-value">{{ records.StudentType }}</span></v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Transaction Details -->
      <v-col cols="12" md="8">
        <v-card class="section-card mb-4 fill-height">
          <div class="section-header">
            <v-icon color="white" class="mr-2">mdi-swap-horizontal</v-icon>
            <span class="section-header-title">Transaction Details</span>
          </div>
          <v-card-text class="pa-2">
            <v-simple-table>
              <thead>
                <tr>
                  <th class="font-weight-bold">Transaction Type</th>
                  <th class="font-weight-bold">Issued Date</th>
                  <th class="font-weight-bold">Issued Amount</th>
                  <th class="font-weight-bold">Remarks</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in records.ScholarshipIssued" :key="index">
                  <td>{{ item.TransactionTypeName }}</td>
                  <td>{{ item.ScholarshipIssuedDate }}</td>
                  <td>{{ item.ScholarshipIssuedAmount }} Rs</td>
                  <td>{{ item.ScholarshipIssuedRemarks }}</td>
                </tr>
                <tr v-if="!records.ScholarshipIssued || records.ScholarshipIssued.length === 0">
                  <td colspan="4" class="text-center">-- NA --</td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Attachments -->
    <v-card class="section-card mb-4">
      <div class="section-header">
        <v-icon color="white" class="mr-2">mdi-paperclip</v-icon>
        <span class="section-header-title">Attachments</span>
      </div>
      <v-card-text class="pa-4">
        <div v-viewer>
          <v-row>
            <v-col cols="12" sm="6" md="3" class="text-center">
              <b>Recommendation Letter</b><br />
              <img :src="records.RecommendationLetterImagePath" style="height:150px;margin-top:10px;cursor:zoom-in" />
              <div class="mt-2">
                <a :href="records.RecommendationLetterImagePath" :download="records.RecommendationLetterImagePath" target="_blank">
                  <v-icon color="primary">mdi-download</v-icon>
                </a>
              </div>
            </v-col>
            <v-col cols="12" sm="6" md="3" class="text-center">
              <b>Income Proof</b><br />
              <img :src="records.IncomeProofImagePath" style="height:150px;margin-top:10px;cursor:zoom-in" />
              <div class="mt-2">
                <a :href="records.IncomeProofImagePath" :download="records.IncomeProofImagePath" target="_blank">
                  <v-icon color="primary">mdi-download</v-icon>
                </a>
              </div>
            </v-col>
            <v-col cols="12" sm="6" md="3" class="text-center">
              <b>Passbook</b><br />
              <img :src="records.PassbookImagePath" style="height:150px;margin-top:10px;cursor:zoom-in" />
              <div class="mt-2">
                <a :href="records.PassbookImagePath" :download="records.PassbookImagePath" target="_blank">
                  <v-icon color="primary">mdi-download</v-icon>
                </a>
              </div>
            </v-col>
            <v-col cols="12" sm="6" md="3" class="text-center">
              <b>Marksheet</b><br />
              <img :src="records.MarksheetImagePath" style="height:150px;margin-top:10px;cursor:zoom-in" />
              <div class="mt-2">
                <a :href="records.MarksheetImagePath" :download="records.MarksheetImagePath" target="_blank">
                  <v-icon color="primary">mdi-download</v-icon>
                </a>
              </div>
            </v-col>
          </v-row>
        </div>
      </v-card-text>
    </v-card>

    <!-- Previous Description -->
    <v-card class="section-card mb-4" v-if="records.DescriptionTxt && records.DescriptionTxt !== ''">
      <div class="section-header">
        <v-icon color="white" class="mr-2">mdi-message</v-icon>
        <span class="section-header-title">Description</span>
      </div>
      <v-card-text class="pa-4">
        <p class="mb-0" v-html="records.DescriptionTxt"></p>
      </v-card-text>
    </v-card>

    <!-- Approval Remarks -->
    <v-card class="section-card mb-4">
      <div class="section-header">
        <v-icon color="white" class="mr-2">mdi-comment-text-outline</v-icon>
        <span class="section-header-title">Approval Remarks <span style="color:#fca5a5">*</span></span>
      </div>
      <v-card-text class="pa-4">
        <v-textarea
          outlined
          v-model="Description"
          :rules="DescriptionRules"
          label="Enter remarks / description"
          dense
          hide-details="auto"
        ></v-textarea>
      </v-card-text>
    </v-card>

    <!-- Action Buttons -->
    <v-row justify="center" class="mb-6">
      <v-col cols="12" class="d-flex flex-wrap justify-center" style="gap:12px">
        <v-btn @click.prevent="submitForm(1)" :loading="ScholarshipLoadingFlag" color="#1db954" elevation="3" rounded class="white--text">
          <v-icon left small>mdi-check-circle</v-icon> Approve for Scholarship
        </v-btn>
        <v-btn @click.prevent="submitForm(2)" :loading="EmpowerHerLoadingFlag" color="#1db954" elevation="3" rounded class="white--text">
          <v-icon left small>mdi-check-circle</v-icon> Approve for Empower Her
        </v-btn>
        <v-btn @click.prevent="submitForm(3)" :loading="RejectLoadingFlag" color="#fa1705" elevation="3" rounded class="white--text">
          <v-icon left small>mdi-close-circle</v-icon> Reject
        </v-btn>
         <v-btn @click.prevent="goBack" color="#3A67B1" elevation="3" rounded class="white--text">
          <v-icon left small>mdi-close-circle</v-icon> Cancel
        </v-btn>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";

export default {
  name: "StudentScholarshipApprovalPage",
  mixins: [common],
  data() {
    return {
      LoadingFlag: false,
      records: {},
      Description: "",
      DescriptionRules: [(v) => !!v || "Description is required"],
      ScholarshipLoadingFlag: false,
      EmpowerHerLoadingFlag: false,
      RejectLoadingFlag: false,
      ResultFlag: false,
    };
  },
  methods: {
    goBack() {
      this.$router.push("/validate-applications/list");
    },
    getStudentDetails() {
      var StudentId = this.$route.query.studentId;
      if (!StudentId) return;

      this.LoadingFlag = true;
      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;

      const thisIns = this;
      this.$http({
        url: server_url + "api/student/show",
        method: "POST",
        data: { UserInterface: 2, Student: StudentId },
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      })
        .then(function (response) {
          thisIns.LoadingFlag = false;
          if (response.data.flag == 1) {
            thisIns.records = response.data.records;
          } else {
            thisIns.sweetAlert("error", response.data.output, false);
          }
        })
        .catch(function (error) {
          console.log("error=" + error);
          thisIns.LoadingFlag = false;
        });
    },
    submitForm(Status) {
      var StudentId = this.$route.query.studentId;
      var ApplicationNumber = this.$route.query.applicationNumber;

      if (Status > 0) {
        if (Status === 1) this.ScholarshipLoadingFlag = true;
        else if (Status === 2) this.EmpowerHerLoadingFlag = true;
        else if (Status === 3) this.RejectLoadingFlag = true;

        this.LoadingFlag = true;
        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;

        const thisIns = this;
        this.$http({
          url: server_url + "api/student/update-approval-status",
          method: "POST",
          data: {
            UserInterface: Status,
            Student: StudentId,
            Description: this.Description,
            ApplicationNumber: ApplicationNumber,
          },
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
        })
          .then(function (response) {
            thisIns.LoadingFlag = false;
            thisIns.ScholarshipLoadingFlag = false;
            thisIns.EmpowerHerLoadingFlag = false;
            thisIns.RejectLoadingFlag = false;
            if (response.data.flag == 1) {
              thisIns.sweetAlert("success", response.data.output, false);
              sessionStorage.setItem("validateApplicationsNeedsRefresh", "1");
              thisIns.goBack();
            } else {
              thisIns.sweetAlert("error", response.data.output, false);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.LoadingFlag = false;
            thisIns.ScholarshipLoadingFlag = false;
            thisIns.EmpowerHerLoadingFlag = false;
            thisIns.RejectLoadingFlag = false;
          });
      } else {
        this.sweetAlert("error", "Kindly fill the required fields", false);
      }
    },
  },
  beforeMount() {
    this.getStudentDetails();
  },
};
</script>

<style scoped>
.approval-page {
  min-height: 100vh;
}

.header-card {
  background: linear-gradient(135deg, #1a3a5c 0%, #0097d7 50%, #1a3a5c 100%) !important;
  border-radius: 16px;
  overflow: hidden;
  position: relative;
  border: none;
  box-shadow: 0 16px 48px rgba(26, 58, 92, 0.45), 0 0 32px rgba(0, 151, 215, 0.2);
}

.header-card::before {
  content: "";
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.07) 50%, transparent 70%);
  pointer-events: none;
}

.header-gradient { position: relative; z-index: 1; }

.header-backdrop-effect {
  position: absolute;
  top: -50%; right: -50%;
  width: 200%; height: 200%;
  background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
  pointer-events: none;
}

.header-content { position: relative; z-index: 2; }

.header-icon-box {
  background: linear-gradient(135deg, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0.1) 100%);
  backdrop-filter: blur(12px);
  border-radius: 14px;
  padding: 12px;
  border: 2px solid rgba(255,255,255,0.22);
  flex-shrink: 0;
}

.header-title {
  font-size: clamp(1.1rem, 3vw, 1.8rem);
  font-weight: 800;
  letter-spacing: 0.4px;
  text-shadow: 1px 1px 5px rgba(0,0,0,0.22);
}

.header-subtitle {
  font-size: clamp(0.78rem, 1.5vw, 0.92rem);
  opacity: 0.88;
  font-weight: 500;
  margin-top: 3px;
}

.back-btn {
  background: rgba(255,255,255,0.15) !important;
  border: 2px solid rgba(255,255,255,0.28) !important;
  color: white !important;
  backdrop-filter: blur(10px);
  font-weight: 600;
}

.back-btn:hover {
  background: rgba(255,255,255,0.24) !important;
}

.section-card {
  border-radius: 12px !important;
  overflow: hidden;
  border: 1px solid #d0dcea;
  box-shadow: 0 2px 12px rgba(26, 58, 92, 0.08) !important;
}

.fill-height {
  height: 100%;
}

.section-header {
  background: linear-gradient(90deg, #1a3a5c 0%, #1e4d78 100%);
  padding: 12px 18px;
  display: flex;
  align-items: center;
}

.section-header-title {
  font-size: 1rem;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: 0.3px;
}

.detail-label {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1a3a5c;
}

.detail-value {
  font-size: 0.95rem;
  color: #2d4a6e;
}

.section-card .v-card__text {
  font-size: 0.95rem;
}

.section-card .v-simple-table th {
  font-size: 0.95rem !important;
  font-weight: 700;
  color: #1a3a5c !important;
}

.section-card .v-simple-table td {
  font-size: 0.93rem !important;
  color: #2d4a6e;
}

.student-img {
  border-radius: 10px;
  border: 2px solid #e0e7ef;
}

.text-center { text-align: center; }

@media (max-width: 960px) {
  .fill-height {
    height: auto;
  }
}
</style>
